import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import {
    FIRM, STATES, UNITS, amountInWords, billProblems, billsCsv, billsSummary, blankBill, blankLine,
    computeBill, isIntraState, localDate, num, numberKey, stateCodeOf, stateFromGstin,
} from "./tax_bill_calc";
import { openPrintWindow, printBill, writeBillTo } from "./tax_bill_print";
import "./tax_bill.css";

// Advance bill generator — campus/uniform se alag. Yahan party aur item
// master me save rehte hain, panel apni invoice series chalata hai, aur har
// bill record me rehta hai (dobara chhapne ke liye).
const COL_PARTIES = "BillParties";
const COL_ITEMS = "BillItems";
const COL_BILLS = "BillInvoices";
const COL_CONFIG = "BillConfig";
const DOC_COUNTER = "counter";

const fmt = (v) => num(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const inr = (v) => `₹${fmt(v)}`;
// Apne ghadi ki tareekh — toISOString() UTC deta tha, raat 12 se subah
// 5:30 tak bill par pichhla din chhapta.
const today = () => localDate();
const dmy = (iso) => {
    const m = String(iso || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : String(iso || "");
};

function Field({ label, value, onChange, type = "text", wide, placeholder, list }) {
    return (
        <label className={`tb-field ${wide ? "wide" : ""}`}>
            <span>{label}</span>
            <input
                type={type}
                value={value == null ? "" : value}
                placeholder={placeholder || ""}
                list={list}
                onChange={(e) => onChange(e.target.value)}
            />
        </label>
    );
}

/* ───────────────────────────── Parties ───────────────────────────── */

function PartiesTab({ parties, busy, run }) {
    const [edit, setEdit] = useState(null);
    const blank = { name: "", address: "", city: "", state: "", gstin: "", mobile: "", email: "" };
    const f = edit || blank;
    const set = (k, v) => setEdit({ ...f, [k]: v });

    const save = () => {
        const name = String(f.name || "").trim();
        if (!name) { alert("Party ka naam chahiye."); return; }
        const data = {
            name, address: f.address || "", city: f.city || "", state: f.state || "",
            stateCode: stateCodeOf(f.state), gstin: String(f.gstin || "").trim().toUpperCase(),
            mobile: f.mobile || "", email: f.email || "", updatedAt: Date.now(),
        };
        run(async () => {
            if (f.id) await db.collection(COL_PARTIES).doc(f.id).set(data, { merge: true });
            else await db.collection(COL_PARTIES).add({ ...data, createdAt: Date.now() });
            setEdit(null);
        }, "Party saved.");
    };

    return (
        <div className="tb-pane">
            <div className="tb-split">
                <div className="tb-list">
                    <div className="tb-list-head">
                        <strong>Parties ({parties.length})</strong>
                        <button type="button" className="tb-btn" onClick={() => setEdit({ ...blank })}>+ New party</button>
                    </div>
                    {parties.length === 0 && <p className="tb-empty">Koi party nahi — "New party" se jodo.</p>}
                    {parties.map((p) => (
                        <button type="button" key={p.id} className={`tb-row ${edit && edit.id === p.id ? "is-on" : ""}`} onClick={() => setEdit(p)}>
                            <strong>{p.name}</strong>
                            <span>{[p.city, p.state].filter(Boolean).join(", ") || "—"}</span>
                            <em>{p.gstin || "no GSTIN"}</em>
                        </button>
                    ))}
                </div>
                {edit && (
                    <div className="tb-form">
                        <h4>{f.id ? "Edit party" : "New party"}</h4>
                        <Field label="Name" value={f.name} onChange={(v) => set("name", v)} wide />
                        <Field label="Address" value={f.address} onChange={(v) => set("address", v)} wide />
                        <Field label="City / PIN line" value={f.city} onChange={(v) => set("city", v)} wide
                            placeholder="IIT Madras Tamil Nadu- 600036" />
                        <Field label="GSTIN" value={f.gstin} onChange={(v) => {
                            const g = v.toUpperCase();
                            const st = stateFromGstin(g);
                            setEdit({ ...f, gstin: g, ...(st && !f.state ? { state: st } : {}) });
                        }} />
                        <label className="tb-field">
                            <span>State (place of supply)</span>
                            <select value={f.state || ""} onChange={(e) => set("state", e.target.value)}>
                                <option value="">— pick —</option>
                                {STATES.map(([s, c]) => <option key={s} value={s}>{s} ({c})</option>)}
                            </select>
                        </label>
                        <Field label="Mobile" value={f.mobile} onChange={(v) => set("mobile", v)} />
                        <Field label="Email" value={f.email} onChange={(v) => set("email", v)} />
                        {f.state && (
                            <p className="tb-note">
                                {isIntraState(f.state)
                                    ? "Apna hi state — bill par CGST + SGST lagega."
                                    : `${f.state} — bill par IGST lagega.`}
                            </p>
                        )}
                        <div className="tb-actions">
                            <button type="button" className="tb-btn tb-btn--pri" disabled={busy} onClick={save}>Save</button>
                            <button type="button" className="tb-btn" onClick={() => setEdit(null)}>Cancel</button>
                            {f.id && (
                                <button type="button" className="tb-btn tb-btn--danger" disabled={busy}
                                    onClick={() => {
                                        if (!window.confirm(`Delete party "${f.name}"? Purane bill waise hi rahenge.`)) return;
                                        run(async () => { await db.collection(COL_PARTIES).doc(f.id).delete(); setEdit(null); }, "Party deleted.");
                                    }}>Delete</button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ────────────────────────────── Items ────────────────────────────── */

function ItemsTab({ items, busy, run }) {
    const [edit, setEdit] = useState(null);
    const blank = { desc: "", hsn: "", rate: "", gstPct: 5, unit: "" };
    const f = edit || blank;
    const set = (k, v) => setEdit({ ...f, [k]: v });

    const save = () => {
        const desc = String(f.desc || "").trim();
        if (!desc) { alert("Item ka naam chahiye."); return; }
        const data = {
            desc, hsn: String(f.hsn || "").trim(), rate: num(f.rate),
            gstPct: num(f.gstPct), unit: f.unit || "", updatedAt: Date.now(),
        };
        run(async () => {
            if (f.id) await db.collection(COL_ITEMS).doc(f.id).set(data, { merge: true });
            else await db.collection(COL_ITEMS).add({ ...data, createdAt: Date.now() });
            setEdit(null);
        }, "Item saved.");
    };

    return (
        <div className="tb-pane">
            <div className="tb-split">
                <div className="tb-list">
                    <div className="tb-list-head">
                        <strong>Items ({items.length})</strong>
                        <button type="button" className="tb-btn" onClick={() => setEdit({ ...blank })}>+ New item</button>
                    </div>
                    {items.length === 0 && <p className="tb-empty">Koi item nahi — "New item" se jodo.</p>}
                    {items.map((it) => (
                        <button type="button" key={it.id} className={`tb-row ${edit && edit.id === it.id ? "is-on" : ""}`} onClick={() => setEdit(it)}>
                            <strong>{it.desc}</strong>
                            <span>HSN {it.hsn || "—"} · GST {num(it.gstPct)}%</span>
                            <em>{it.rate ? inr(it.rate) : "rate —"}</em>
                        </button>
                    ))}
                </div>
                {edit && (
                    <div className="tb-form">
                        <h4>{f.id ? "Edit item" : "New item"}</h4>
                        <Field label="Description of goods" value={f.desc} onChange={(v) => set("desc", v)} wide
                            placeholder="T SHIRT ROUND NECK" />
                        <Field label="HSN / SAC" value={f.hsn} onChange={(v) => set("hsn", v)} placeholder="6109" />
                        <Field label="Default rate" value={f.rate} onChange={(v) => set("rate", v)} type="number" />
                        <label className="tb-field">
                            <span>GST %</span>
                            <select value={f.gstPct} onChange={(e) => set("gstPct", e.target.value)}>
                                {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                            </select>
                        </label>
                        <label className="tb-field">
                            <span>Unit</span>
                            <select value={f.unit || "PCS"} onChange={(e) => set("unit", e.target.value)}>
                                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </label>
                        <div className="tb-actions">
                            <button type="button" className="tb-btn tb-btn--pri" disabled={busy} onClick={save}>Save</button>
                            <button type="button" className="tb-btn" onClick={() => setEdit(null)}>Cancel</button>
                            {f.id && (
                                <button type="button" className="tb-btn tb-btn--danger" disabled={busy}
                                    onClick={() => {
                                        if (!window.confirm(`Delete item "${f.desc}"?`)) return;
                                        run(async () => { await db.collection(COL_ITEMS).doc(f.id).delete(); setEdit(null); }, "Item deleted.");
                                    }}>Delete</button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ───────────────────────────── Builder ───────────────────────────── */

function BuilderTab({ bill, setBill, parties, items, busy, run, onSaved, editingId }) {
    const t = useMemo(() => computeBill(bill), [bill]);
    const problems = useMemo(() => billProblems(bill), [bill]);
    const set = (k, v) => setBill((b) => ({ ...b, [k]: v }));
    const setLine = (i, patch) => setBill((b) => ({
        ...b, lines: b.lines.map((l, x) => (x === i ? { ...l, ...patch } : l)),
    }));

    const pickParty = (id) => {
        const p = parties.find((x) => x.id === id);
        if (!p) { set("party", { name: "", address: "", city: "", state: "", gstin: "", mobile: "" }); return; }
        set("party", {
            id: p.id, name: p.name || "", address: p.address || "", city: p.city || "",
            state: p.state || "", gstin: p.gstin || "", mobile: p.mobile || "",
        });
    };
    const pickItem = (i, id) => {
        const it = items.find((x) => x.id === id);
        if (!it) return;
        setLine(i, { desc: it.desc, hsn: it.hsn || "", rate: it.rate || "", gstPct: num(it.gstPct), unit: it.unit || "PCS" });
    };

    // Builder me type kiya party/item seedhe master me — tab badalne ki zaroorat nahi.
    const partyNameTaken = (name) => parties.some((p) => String(p.name || "").trim().toLowerCase() === String(name || "").trim().toLowerCase());
    const itemKnown = (desc) => items.some((x) => String(x.desc || "").trim().toLowerCase() === String(desc || "").trim().toLowerCase());
    const savePartyToMaster = () => {
        const pp = bill.party || {};
        const name = String(pp.name || "").trim();
        if (!name) return;
        if (partyNameTaken(name)) { alert(`"${name}" naam ki party pehle se hai — upar dropdown se chuno.`); return; }
        run(async () => {
            const ref = await db.collection(COL_PARTIES).add({
                name, address: pp.address || "", city: pp.city || "", state: pp.state || "",
                stateCode: stateCodeOf(pp.state), gstin: String(pp.gstin || "").trim().toUpperCase(),
                mobile: pp.mobile || "", email: "", createdAt: Date.now(), updatedAt: Date.now(),
            });
            set("party", { ...pp, id: ref.id });
        }, "Party master me save ho gayi.");
    };
    const saveItemToMaster = (l) => {
        const desc = String(l.desc || "").trim();
        if (!desc || itemKnown(desc)) return;
        run(() => db.collection(COL_ITEMS).add({
            desc, hsn: String(l.hsn || "").trim(), rate: num(l.rate), gstPct: num(l.gstPct),
            unit: l.unit || "PCS", createdAt: Date.now(), updatedAt: Date.now(),
        }), "Item master me save ho gaya.");
    };

    const save = async (alsoPrint) => {
        if (problems.length) { alert(`Pehle ye theek karo:\n\n• ${problems.join("\n• ")}`); return; }
        // Print window abhi kholo — click ke andar. Save ke baad kholte to
        // browser popup samajh kar rok deta.
        const w = alsoPrint ? openPrintWindow() : null;
        if (alsoPrint && !w) return;
        const totals = computeBill(bill);
        const doc = {
            ...bill,
            no: String(bill.no).trim(),
            noNum: num(bill.no),
            lines: bill.lines.filter((l) => String(l.desc || "").trim()),
            totals: {
                gross: totals.gross, totalAdd: totals.totalAdd, totalLess: totals.totalLess,
                taxable: totals.taxable, cgst: totals.cgst, sgst: totals.sgst, igst: totals.igst,
                addTax: totals.addTax, tcs: totals.tcs, roundOff: totals.roundOff, net: totals.net,
            },
            intra: totals.intra,
            updatedAt: Date.now(),
        };
        let saved = false;
        await run(async () => {
            const billRef = editingId
                ? db.collection(COL_BILLS).doc(editingId)
                : db.collection(COL_BILLS).doc();
            const key = numberKey(doc.no);

            // GST me invoice number unique hona zaroori. Purane bill (jinka
            // number reserve nahi hua) query se pakdo...
            const same = await db.collection(COL_BILLS).where("no", "==", doc.no).get();
            if (same.docs.some((d) => d.id !== billRef.id)) {
                throw new Error(`Invoice #${doc.no} pehle se bana hai — doosra number do`);
            }

            // ...aur ek hi waqt do log same number save karein to transaction
            // me number "reserve" karke pakdo. Bill, reservation aur series —
            // teeno ek saath likhte hain, ya koi nahi.
            const usedCol = db.collection(COL_CONFIG).doc("numbers").collection("used");
            await db.runTransaction(async (tx) => {
                const usedRef = usedCol.doc(key);
                const cref = db.collection(COL_CONFIG).doc(DOC_COUNTER);
                const usedSnap = await tx.get(usedRef);
                const cSnap = await tx.get(cref);
                let oldUsedRef = null;
                if (editingId) {
                    const oldSnap = await tx.get(billRef);
                    const oldNo = oldSnap.exists ? String((oldSnap.data() || {}).no || "") : "";
                    if (oldNo && numberKey(oldNo) !== key) {
                        const r = usedCol.doc(numberKey(oldNo));
                        const rs = await tx.get(r);
                        if (rs.exists && (rs.data() || {}).billId === billRef.id) oldUsedRef = r;
                    }
                }
                if (usedSnap.exists && (usedSnap.data() || {}).billId !== billRef.id) {
                    throw new Error(`Invoice #${doc.no} abhi kisi aur ne le liya — doosra number do`);
                }
                tx.set(billRef, editingId ? doc : { ...doc, createdAt: Date.now() }, { merge: true });
                tx.set(usedRef, { billId: billRef.id, no: doc.no, at: Date.now() });
                if (oldUsedRef) tx.delete(oldUsedRef);
                // Series aage badhao — sirf tab jab is bill ne agla number liya ho.
                const next = num((cSnap.data() || {}).nextNo) || 1;
                if (doc.noNum >= next) tx.set(cref, { nextNo: doc.noNum + 1 }, { merge: true });
            });
            if (!editingId) onSaved(billRef.id);
            saved = true;
        }, alsoPrint ? "Bill saved — print khul raha hai." : "Bill saved.");
        if (w) {
            if (saved) writeBillTo(w, bill);
            else w.close();
        }
    };

    return (
        <div className="tb-pane">
            <div className="tb-build">
                <section className="tb-card">
                    <h4>Invoice</h4>
                    <div className="tb-grid">
                        <Field label="Invoice No." value={bill.no} onChange={(v) => set("no", v)} />
                        <Field label="Date" value={bill.date} onChange={(v) => set("date", v)} type="date" />
                        <Field label="Reference No. & date" value={bill.refNo} onChange={(v) => set("refNo", v)} />
                        <Field label="Other references" value={bill.otherRef} onChange={(v) => set("otherRef", v)} />
                        <Field label="Buyer's Order No." value={bill.buyerOrderNo} onChange={(v) => set("buyerOrderNo", v)} />
                        <Field label="Dated" value={bill.buyerOrderDate} onChange={(v) => set("buyerOrderDate", v)} type="date" />
                        <Field label="Dispatch Doc No." value={bill.dispatchDocNo} onChange={(v) => set("dispatchDocNo", v)} />
                        <Field label="Delivery Note Date" value={bill.deliveryNoteDate} onChange={(v) => set("deliveryNoteDate", v)} type="date" />
                        <Field label="Dispatched Through" value={bill.dispatchedThrough} onChange={(v) => set("dispatchedThrough", v)} />
                        <Field label="Destination" value={bill.destination} onChange={(v) => set("destination", v)} />
                        <Field label="Bill of Lading / LR-RR No." value={bill.lrNo} onChange={(v) => set("lrNo", v)} />
                        <Field label="Motor Vehicle No." value={bill.vehicleNo} onChange={(v) => set("vehicleNo", v)} />
                        <Field label="Terms of Delivery" value={bill.termsOfDelivery} onChange={(v) => set("termsOfDelivery", v)} wide />
                    </div>
                </section>

                <section className="tb-card">
                    <h4>Buyer (Bill To)</h4>
                    <label className="tb-field wide">
                        <span>Party</span>
                        <select value={(bill.party && bill.party.id) || ""} onChange={(e) => pickParty(e.target.value)}>
                            <option value="">— select party —</option>
                            {parties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.city ? ` · ${p.city}` : ""}</option>)}
                        </select>
                    </label>
                    <div className="tb-grid">
                        <Field label="Name" value={bill.party.name} onChange={(v) => set("party", { ...bill.party, name: v })} wide />
                        <Field label="Address" value={bill.party.address} onChange={(v) => set("party", { ...bill.party, address: v })} wide />
                        <Field label="City / PIN line" value={bill.party.city} onChange={(v) => set("party", { ...bill.party, city: v })} wide />
                        <Field label="Mobile" value={bill.party.mobile} onChange={(v) => set("party", { ...bill.party, mobile: v })} />
                        <Field label="GSTIN" value={bill.party.gstin} onChange={(v) => {
                            const g = v.toUpperCase();
                            const st = stateFromGstin(g);
                            set("party", { ...bill.party, gstin: g, ...(st ? { state: st } : {}) });
                        }} />
                        <label className="tb-field">
                            <span>State</span>
                            <select value={bill.party.state || ""} onChange={(e) => set("party", { ...bill.party, state: e.target.value })}>
                                <option value="">— pick —</option>
                                {STATES.map(([s, c]) => <option key={s} value={s}>{s} ({c})</option>)}
                            </select>
                        </label>
                    </div>
                    <p className={`tb-tax-mode ${t.intra ? "is-intra" : "is-inter"}`}>
                        {t.intra
                            ? `Place of supply ${bill.party.state || FIRM.state} (${stateCodeOf(bill.party.state || FIRM.state)}) — CGST + SGST`
                            : `Place of supply ${bill.party.state} (${stateCodeOf(bill.party.state)}) — IGST`}
                    </p>
                    {!bill.party.id && String(bill.party.name || "").trim() && (
                        <button type="button" className="tb-btn tb-save-master" disabled={busy} onClick={savePartyToMaster}>
                          + Is party ko master me save karo
                        </button>
                    )}
                </section>

                <section className="tb-card tb-card--wide">
                    <h4>Items</h4>
                    <div className="tb-items-wrap">
                        <table className="tb-items">
                            <thead>
                                <tr>
                                    <th>#</th><th>Description of goods</th><th>HSN/SAC</th><th>Qty</th><th>Unit</th>
                                    <th>Rate</th><th>Disc%</th><th>GST%</th><th className="r">Amount</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {bill.lines.map((l, i) => {
                                    const calc = t.lines[i];
                                    return (
                                        <tr key={i}>
                                            <td className="c">{i + 1}</td>
                                            <td>
                                                <input className="tb-in tb-in--desc" value={l.desc}
                                                    onChange={(e) => setLine(i, { desc: e.target.value })} placeholder="item" />
                                                <div className="tb-line-tools">
                                                    <select className="tb-pick" value="" onChange={(e) => pickItem(i, e.target.value)}>
                                                        <option value="">+ from items</option>
                                                        {items.map((it) => <option key={it.id} value={it.id}>{it.desc}</option>)}
                                                    </select>
                                                    {String(l.desc || "").trim() && !itemKnown(l.desc) && (
                                                        <button type="button" className="tb-pick tb-pick--save" disabled={busy}
                                                            onClick={() => saveItemToMaster(l)}>save item</button>
                                                    )}
                                                </div>
                                            </td>
                                            <td><input className="tb-in tb-in--s" value={l.hsn} onChange={(e) => setLine(i, { hsn: e.target.value })} /></td>
                                            <td><input className="tb-in tb-in--s" type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></td>
                                            <td>
                                                <select className="tb-in tb-in--xs" value={l.unit || "PCS"} onChange={(e) => setLine(i, { unit: e.target.value })}>
                                                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                                                </select>
                                            </td>
                                            <td><input className="tb-in tb-in--s" type="number" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} /></td>
                                            <td><input className="tb-in tb-in--xs" type="number" value={l.discPct} onChange={(e) => setLine(i, { discPct: e.target.value })} /></td>
                                            <td>
                                                <select className="tb-in tb-in--xs" value={l.gstPct} onChange={(e) => setLine(i, { gstPct: e.target.value })}>
                                                    {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}</option>)}
                                                </select>
                                            </td>
                                            <td className="r b">{calc ? fmt(calc.base) : ""}</td>
                                            <td>
                                                <button type="button" className="tb-x" title="Remove line"
                                                    onClick={() => setBill((b) => ({
                                                        ...b, lines: b.lines.length > 1 ? b.lines.filter((_, x) => x !== i) : [blankLine()],
                                                    }))}>×</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <button type="button" className="tb-btn" onClick={() => setBill((b) => ({ ...b, lines: [...b.lines, blankLine()] }))}>
                        + Add line
                    </button>
                </section>

                <section className="tb-card">
                    <h4>Charges</h4>
                    <div className="tb-grid">
                        <Field label="Counter Bill" value={bill.counterBill} onChange={(v) => set("counterBill", v)} />
                        <Field label="Packing" value={bill.packing} onChange={(v) => set("packing", v)} type="number" />
                        <Field label="Transport" value={bill.transport} onChange={(v) => set("transport", v)} type="number" />
                        <Field label="ADD" value={bill.addAmt} onChange={(v) => set("addAmt", v)} type="number" />
                        <Field label="MISC" value={bill.misc} onChange={(v) => set("misc", v)} type="number" />
                        <Field label="TOTAL LESS" value={bill.totalLess} onChange={(v) => set("totalLess", v)} type="number" />
                        <Field label="TCS" value={bill.tcs} onChange={(v) => set("tcs", v)} type="number" />
                    </div>
                    <label className="tb-check">
                        <input type="checkbox" checked={bill.taxCharges !== false}
                            onChange={(e) => set("taxCharges", e.target.checked)} />
                        <span>Charges par bhi GST lagao (packing/transport supply ki value ka hissa hain)</span>
                    </label>
                    <Field label="Extra note on bill" value={bill.notes} onChange={(v) => set("notes", v)} wide />
                </section>

                <section className="tb-card tb-total">
                    <h4>Total</h4>
                    <div className="tb-trow"><span>GROSS</span><b>{fmt(t.gross)}</b></div>
                    {t.totalAdd > 0 && <div className="tb-trow"><span>TOTAL ADD</span><b>{fmt(t.totalAdd)}</b></div>}
                    {t.totalLess > 0 && <div className="tb-trow"><span>TOTAL LESS</span><b>− {fmt(t.totalLess)}</b></div>}
                    {t.rateRows.map((g) => (
                        <div className="tb-trow tb-trow--tax" key={g.rate}>
                            <span>{t.intra ? `CGST ${g.rate / 2}% + SGST ${g.rate / 2}%` : `IGST ${g.rate}%`} on {fmt(g.taxable)}</span>
                            <b>{fmt(g.tax)}</b>
                        </div>
                    ))}
                    <div className="tb-trow"><span>Add Tax</span><b>{fmt(t.addTax)}</b></div>
                    {t.tcs > 0 && <div className="tb-trow"><span>TCS</span><b>{fmt(t.tcs)}</b></div>}
                    {Math.abs(t.roundOff) >= 0.005 && (
                        <div className="tb-trow"><span>Round Off</span><b>{t.roundOff > 0 ? "+" : "−"} {fmt(Math.abs(t.roundOff))}</b></div>
                    )}
                    <div className="tb-net"><span>Net Payable</span><b>{inr(t.net)}</b></div>
                    <p className="tb-words">{amountInWords(t.net)}</p>

                    {problems.length > 0 && (
                        <ul className="tb-problems">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
                    )}
                    <div className="tb-actions">
                        <button type="button" className="tb-btn tb-btn--pri" disabled={busy || problems.length > 0}
                            onClick={() => save(true)}>Save &amp; print</button>
                        <button type="button" className="tb-btn" disabled={busy || problems.length > 0}
                            onClick={() => save(false)}>Save only</button>
                        <button type="button" className="tb-btn" disabled={problems.length > 0}
                            onClick={() => printBill(bill)}>Preview (no save)</button>
                    </div>
                    {editingId && <p className="tb-note">Purana bill khula hai — Save karne par wahi update hoga, naya number nahi lega.</p>}
                </section>
            </div>
        </div>
    );
}

/* ───────────────────────────── History ───────────────────────────── */

function BillsTab({ bills, onOpen, onCopy, busy, run }) {
    const [q, setQ] = useState("");
    const [month, setMonth] = useState("");
    const list = useMemo(() => {
        const s = q.trim().toLowerCase();
        return bills.filter((b) => {
            if (month && String(b.date || "").slice(0, 7) !== month) return false;
            if (!s) return true;
            return [b.no, b.party && b.party.name, b.party && b.party.gstin, b.date].join(" ").toLowerCase().includes(s);
        });
    }, [bills, q, month]);
    const sum = useMemo(() => billsSummary(list), [list]);

    const exportCsv = () => {
        const blob = new Blob(["\ufeff" + billsCsv(list)], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tax-invoices-${month || "all"}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    // Tax invoice delete nahi hota — series me gap aata hai aur GST me
    // cancelled bill bhi hisaab me dikhna chahiye. Cancel karo, wapas bhi ho sakta.
    const toggleCancel = (b) => {
        const on = !b.cancelled;
        if (on && !window.confirm(`Invoice #${b.no} cancel karein?\n\nNumber series me rahega, bill par CANCELLED chhapega, aur jod me nahi ginega. Baad me wapas chalu kar sakte ho.`)) return;
        run(() => db.collection(COL_BILLS).doc(b.id).set(
            { cancelled: on, cancelledAt: on ? Date.now() : null, updatedAt: Date.now() }, { merge: true },
        ), on ? `Invoice #${b.no} cancelled.` : `Invoice #${b.no} wapas chalu.`);
    };

    return (
        <div className="tb-pane">
            <div className="tb-list-head">
                <strong>Bills ({list.length})</strong>
                <label className="tb-month">
                    <span>Mahina</span>
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
                    {month && <button type="button" className="tb-btn" onClick={() => setMonth("")}>Sab</button>}
                </label>
                <input className="tb-search" placeholder="invoice no / party / GSTIN" value={q} onChange={(e) => setQ(e.target.value)} />
                <button type="button" className="tb-btn" disabled={!list.length} onClick={exportCsv}>Export CSV</button>
            </div>
            {list.length > 0 && (
                <div className="tb-sum">
                    <div><span>Bills</span><b>{sum.count}{sum.cancelled ? <em> +{sum.cancelled} cancelled</em> : null}</b></div>
                    <div><span>Taxable</span><b>{inr(sum.taxable)}</b></div>
                    <div><span>CGST</span><b>{inr(sum.cgst)}</b></div>
                    <div><span>SGST</span><b>{inr(sum.sgst)}</b></div>
                    <div><span>IGST</span><b>{inr(sum.igst)}</b></div>
                    <div className="tb-sum-net"><span>Net</span><b>{inr(sum.net)}</b></div>
                </div>
            )}
            {list.length === 0 && <p className="tb-empty">Koi bill nahi.</p>}
            <div className="tb-bills">
                {list.map((b) => (
                    <div className={`tb-bill ${b.cancelled ? "is-cancelled" : ""}`} key={b.id}>
                        <div className="tb-bill-main">
                            <strong>#{b.no}</strong>
                            {b.cancelled && <span className="tb-tag is-cancelled">CANCELLED</span>}
                            <span className="tb-bill-party">{(b.party && b.party.name) || "—"}</span>
                            <span className="tb-bill-date">{dmy(b.date)}</span>
                            <span className="tb-bill-net">{inr((b.totals && b.totals.net) || 0)}</span>
                            <span className={`tb-tag ${b.intra ? "is-intra" : "is-inter"}`}>{b.intra ? "CGST+SGST" : "IGST"}</span>
                        </div>
                        <div className="tb-bill-acts">
                            <button type="button" className="tb-btn" onClick={() => printBill(b)}>Print</button>
                            <button type="button" className="tb-btn" onClick={() => onOpen(b)}>Open</button>
                            <button type="button" className="tb-btn" onClick={() => onCopy(b)}>Copy</button>
                            <button type="button" className={`tb-btn ${b.cancelled ? "" : "tb-btn--danger"}`} disabled={busy}
                                onClick={() => toggleCancel(b)}>{b.cancelled ? "Restore" : "Cancel"}</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ────────────────────────────── Page ─────────────────────────────── */

export default function TaxBillPage() {
    const [tab, setTab] = useState("build");
    const [parties, setParties] = useState([]);
    const [items, setItems] = useState([]);
    const [bills, setBills] = useState([]);
    const [nextNo, setNextNo] = useState(1);
    const [bill, setBill] = useState(blankBill);
    const [editingId, setEditingId] = useState("");
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState("");
    const seeded = useRef(false);
    const timer = useRef(null);

    const say = useCallback((msg) => {
        setToast(msg);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setToast(""), 3500);
    }, []);
    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

    const run = useCallback(async (fn, okMsg) => {
        setBusy(true);
        try { await fn(); if (okMsg) say(okMsg); }
        catch (e) { console.log("tax bill", e); say(`Nahi hua: ${e.message || e}`); }
        finally { setBusy(false); }
    }, [say]);

    useEffect(() => {
        const sort = (a, b) => String(a.name || a.desc || "").localeCompare(String(b.name || b.desc || ""));
        const u1 = db.collection(COL_PARTIES).onSnapshot(
            (s) => setParties(s.docs.map((d) => ({ id: d.id, ...d.data() })).sort(sort)),
            (e) => console.log("parties", e));
        const u2 = db.collection(COL_ITEMS).onSnapshot(
            (s) => setItems(s.docs.map((d) => ({ id: d.id, ...d.data() })).sort(sort)),
            (e) => console.log("items", e));
        const u3 = db.collection(COL_BILLS).onSnapshot(
            (s) => setBills(s.docs.map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => num(b.noNum) - num(a.noNum) || num(b.createdAt) - num(a.createdAt))),
            (e) => console.log("bills", e));
        const u4 = db.collection(COL_CONFIG).doc(DOC_COUNTER).onSnapshot(
            (d) => setNextNo(num((d.data() || {}).nextNo) || 1),
            (e) => console.log("counter", e));
        return () => { u1(); u2(); u3(); u4(); };
    }, []);

    // Pehli baar khulte hi agla number bhar do (user badal sakta hai).
    useEffect(() => {
        if (seeded.current || !nextNo) return;
        seeded.current = true;
        setBill((b) => (b.no ? b : { ...b, no: String(nextNo) }));
    }, [nextNo]);

    const freshBill = () => {
        setBill({ ...blankBill(), no: String(nextNo), date: today() });
        setEditingId("");
        setTab("build");
    };
    const openBill = (b) => {
        const { id, createdAt, updatedAt, totals, noNum, intra, ...rest } = b;
        setBill({ ...blankBill(), ...rest, lines: (rest.lines || []).length ? rest.lines : [blankLine()] });
        setEditingId(id);
        setTab("build");
    };
    const copyBill = (b) => {
        const { id, createdAt, updatedAt, totals, noNum, intra, ...rest } = b;
        setBill({ ...blankBill(), ...rest, no: String(nextNo), date: today(), lines: (rest.lines || []).length ? rest.lines : [blankLine()] });
        setEditingId("");
        setTab("build");
    };

    return (
        <div className="tb-page">
            <div className="tb-tabs">
                {[["build", editingId ? `Bill #${bill.no}` : "New bill"], ["bills", `Bills (${bills.length})`],
                ["parties", `Parties (${parties.length})`], ["items", `Items (${items.length})`]].map(([k, label]) => (
                    <button type="button" key={k} className={`tb-tab ${tab === k ? "is-on" : ""}`} onClick={() => setTab(k)}>{label}</button>
                ))}
                <span className="tb-spacer" />
                <span className="tb-next">Next no: <b>{nextNo}</b></span>
                <button type="button" className="tb-btn" onClick={freshBill}>+ New bill</button>
            </div>

            {toast && <div className="tb-toast">{toast}</div>}

            {tab === "build" && (
                <BuilderTab bill={bill} setBill={setBill} parties={parties} items={items}
                    busy={busy} run={run} editingId={editingId} onSaved={(id) => { setEditingId(id); }} />
            )}
            {tab === "bills" && <BillsTab bills={bills} onOpen={openBill} onCopy={copyBill} busy={busy} run={run} />}
            {tab === "parties" && <PartiesTab parties={parties} busy={busy} run={run} />}
            {tab === "items" && <ItemsTab items={items} busy={busy} run={run} />}
        </div>
    );
}
